// MP-OS-210: what an export covers. The trend CSV writes it as a "Selection" block above its
// table; the server writes the same block in the articles CSV (main.py _selection_rows), and
// both file names carry the filter slug. One list of filters, in the screen's order.
import { displayLabel } from '../displayLabels'
import { todayIso } from '../format'
import type { ExportFilenameFilter } from './exportFilename'

/** The Sales Trends filter state; 'All' (or no brands) means the filter is not active. */
export interface TrendFilterState {
  brands: readonly string[]
  strength: string
  fmt: string
  category: string
  flavour: string
  nicotine: string
}

export interface ExportFilter extends ExportFilenameFilter {
  label: string
}

/** The active filters as (key, Selection label, shown value): Brand, Strength, Format, Category,
 * Flavour, Nicotine. Mirrors main.py _export_filters. */
export function exportFilters(s: TrendFilterState): ExportFilter[] {
  const on = (v: string) => v !== 'All' && v !== ''
  const all: ExportFilter[] = [
    { key: 'brand', label: 'Brand', value: s.brands.join(', ') },
    { key: 'strength', label: 'Strength', value: on(s.strength) ? s.strength : '' },
    { key: 'format', label: 'Format', value: on(s.fmt) ? s.fmt : '' },
    { key: 'category', label: 'Category', value: on(s.category) ? displayLabel(s.category) : '' },
    { key: 'flavour', label: 'Flavour', value: on(s.flavour) ? s.flavour : '' },
    { key: 'nicotine', label: 'Nicotine', value: on(s.nicotine) ? `${s.nicotine} mg/ml` : '' },
  ]
  return all.filter((f) => f.value)
}

export interface ExportSelection {
  supplier: string | null // display name; null = the whole market
  from: string
  to: string
  segment: string | null // display label; null = all segments
  filters: readonly ExportFilter[]
  unit: string
  frequency?: string // trend CSV only
  partialShown?: boolean // trend CSV only
  coverage?: readonly [string, string][] // trend CSV only (MP-OS-198): "Format coverage below 95 %"
  generated?: string // ISO date; defaults to today (local)
}

/** The Selection block's rows (label, value), before the formula guard and the empty line. */
export function selectionRows(sel: ExportSelection): [string, string][] {
  return [
    ['Selection', ''],
    ['Supplier', sel.supplier || 'All suppliers (whole market)'],
    ['Period', `${sel.from} – ${sel.to}`],
    ['Segment', sel.segment || 'All segments'],
    ...(sel.filters.length ? sel.filters.map((f): [string, string] => [f.label, f.value]) : [['Filters', 'None'] as [string, string]]),
    ...(sel.coverage ?? []),
    ['Unit', sel.unit],
    ...(sel.frequency ? [['Frequency', sel.frequency] as [string, string]] : []),
    ...(sel.partialShown !== undefined ? [['Partial periods', sel.partialShown ? 'Shown' : 'Hidden'] as [string, string]] : []),
    ['Generated', sel.generated ?? todayIso()],
  ]
}
